import { nextTick } from "vue";
import {
  createRouter,
  createMemoryHistory,
  createWebHistory,
  createWebHashHistory,
  isNavigationFailure,
} from "vue-router";
import { analytics } from "../boot/firebase";

import routes from "./routes";
import { isChunkLoadError } from "./chunkReload";

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

// When navigating to a route that includes both a page and a dialog
// (has a hash), strip the hash and navigate to the page first, then
// push the dialog as a separate history entry. This ensures closing
// the dialog returns the user to the page rather than the previous page.
// Skip this splitting for popstate (back/forward) navigations so we
// don't create duplicate history entries when returning to a page#dialog.
let pendingHash = null;
let settingHash = false;
let isPopstate = false;
let hasLoggedInitialPageView = false;

if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    isPopstate = true;
  });
}

// Singleton — both the Quasar boot factory and the named `router` export
// resolve to the same instance. Mirrors the store-export pattern in
// src/store/index.js so non-component callers (classes/, pdf/, util/, ...)
// can `import { router } from "src/router"` and get a real Router.
let routerInstance = null;

function getRouter() {
  if (routerInstance) {
    return routerInstance;
  }
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === "history"
    ? createWebHistory
    : createWebHashHistory;

  const Router = createRouter({
    // scrollBehavior: (to, from) => {
    //   if (to.path !== from.path) {
    //     return { left: 0, top: 0 };
    //   }
    // },
    routes,

    // Leave this as is and make changes in quasar.config.js instead!
    // quasar.config.js -> build -> vueRouterMode
    history: createHistory(process.env.VUE_ROUTER_BASE),
  });

  // Suppress NavigationDuplicated / redirected failures from programmatic
  // navigations (Vue Router 4 rejects push/replace with a NavigationFailure
  // for these; we treat them as no-ops).
  const originalPush = Router.push.bind(Router);
  Router.push = (location) =>
    originalPush(location).catch((err) => {
      if (err && !isNavigationFailure(err)) throw err;
    });

  const originalReplace = Router.replace.bind(Router);
  Router.replace = (location) =>
    originalReplace(location).catch((err) => {
      if (err && !isNavigationFailure(err)) throw err;
    });

  Router.beforeEach((to, from) => {
    const poppedState = isPopstate;
    isPopstate = false;

    // Normalize hashes from old-format URLs like /issues/#id/view
    // where the / belonged before the # (should be /issues#/id/view)
    if (to.hash && !to.hash.startsWith("#/")) {
      return {
        path: to.path,
        query: to.query,
        hash: "#/" + to.hash.slice(1),
      };
    }

    const samePage =
      to.path.replace(/\/$/, "") === from.path.replace(/\/$/, "");
    if (settingHash) {
      settingHash = false;
      return true;
    }
    if (to.hash && !samePage && !poppedState) {
      pendingHash = to.hash;
      return { path: to.path, query: to.query, hash: "" };
    }
    return true;
  });

  Router.afterEach((to, from) => {
    let restoredHash = "";
    if (pendingHash) {
      const hash = pendingHash;
      restoredHash = hash;
      pendingHash = null;
      nextTick(() => {
        settingHash = true;
        window.location.hash = hash;
      });
    }

    if (!analytics) {
      return;
    }

    const toPath = (to.fullPath || to.path || "").split("#")[0];
    const fromPath = (from.fullPath || from.path || "").split("#")[0];
    const dialogHash = restoredHash || to.hash || "";

    if (
      dialogHash.startsWith("#/") &&
      (restoredHash || dialogHash !== (from.hash || "") || toPath !== fromPath)
    ) {
      try {
        analytics.logEvent("dialog_view", {
          dialog_route: dialogHash.slice(1),
          page_path: toPath,
        });
      } catch (error) {
        console.error(error);
      }
    }

    if (!hasLoggedInitialPageView) {
      hasLoggedInitialPageView = true;
      return;
    }

    if (toPath === fromPath) {
      return;
    }

    try {
      analytics.logEvent("page_view", {
        page_title: document.title,
        page_location: window.location.href,
        page_path: toPath,
      });
    } catch (error) {
      console.error(error);
    }
  });

  // ─── Stale-chunk recovery (production builds only) ───────────────────────
  // When a lazy route's chunk fails to load (almost always because a deploy
  // replaced the hashed filenames while this tab was open), do a single full
  // reload to fetch the current index.html + chunk manifest. A sessionStorage
  // guard — which survives the reload — prevents an infinite loop if the
  // failure is not deploy-related (offline, a genuinely missing asset).
  //
  // Gated to production: under `quasar dev` there are no hashed chunks and Vite
  // serves modules over native ESM + HMR, where transient `vite:preloadError`
  // events and slow dynamic-import fetches are normal and self-healing. Reacting
  // to them with a reload would interrupt in-flight navigations (it broke the
  // pdfmake-heavy quote route in E2E). Stale-chunk staleness only exists in a
  // built artifact, so the handler only belongs there.
  if (typeof window !== "undefined" && !process.env.DEV) {
    const RELOAD_FLAG = "na:chunk-reload";
    const reloadOnce = () => {
      let alreadyTried = false;
      try {
        alreadyTried = window.sessionStorage.getItem(RELOAD_FLAG) === "1";
        window.sessionStorage.setItem(RELOAD_FLAG, "1");
      } catch (e) {
        // sessionStorage unavailable (private mode / quota) — proceed; worst
        // case is one extra reload, never an infinite loop within this tab.
      }
      if (!alreadyTried) {
        window.location.reload();
      }
    };

    Router.onError((error) => {
      if (isChunkLoadError(error)) {
        reloadOnce();
      }
    });

    // Vite fires this when its preload helper can't fetch a code-split chunk.
    // preventDefault() stops the default unhandled-rejection throw.
    window.addEventListener("vite:preloadError", (event) => {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      reloadOnce();
    });

    // A completed navigation means we're on fresh chunks — clear the guard so a
    // later, unrelated stale-chunk failure in the same session can recover too.
    Router.afterEach(() => {
      try {
        window.sessionStorage.removeItem(RELOAD_FLAG);
      } catch (e) {
        /* ignore */
      }
    });
  }

  routerInstance = Router;
  return Router;
}

export default function (/* { store, ssrContext } */) {
  return getRouter();
}

// Named export for direct imports in classes / pdf / store actions etc.
// Module-level call; relies on routes/ resolving without circular pitfalls
// (it does today — routes import from the store which is also a singleton).
export const router = getRouter();
